/**
 * Reset Password Page – set a new password using a token from the reset email
 */

import React, { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { resetPassword, validatePassword } from '../services/authService';
import '../styles/auth.css';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Ungültiger oder fehlender Reset-Token');
      return;
    }

    const pwErr = validatePassword(newPassword);
    if (pwErr) {
      setError(pwErr);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      return;
    }

    setIsLoading(true);
    try {
      await resetPassword(token, newPassword);
      setSuccess(true);
      setTimeout(() => {
        navigate('/#lab-auth');
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Passwort zurücksetzen fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-error-card">
            <div className="auth-error-icon">
              <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
            </div>
            <h2>Ungültiger Link</h2>
            <p>Dieser Link zum Zurücksetzen des Passworts ist ungültig oder abgelaufen.</p>
            <div className="auth-error-actions">
              <Link to="/#lab-auth" className="auth-button">
                Zur Anmeldung
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-success-card">
            <div className="auth-success-icon">
              <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="#22c55e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            </div>
            <h2>Passwort erfolgreich zurückgesetzt!</h2>
            <p>Sie können sich jetzt mit Ihrem neuen Passwort anmelden.</p>
            <p className="auth-redirect-notice">
              Sie werden in wenigen Sekunden zur Anmeldeseite weitergeleitet...
            </p>
            <Link to="/#lab-auth" className="auth-button" style={{ marginTop: '1rem' }}>
              Jetzt anmelden
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <h1 className="auth-logo">
            <svg viewBox="0 0 24 26" width="28" height="30" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1L1 11v14h6v-8h10v8h6V11L12 1z" fill="rgba(55, 65, 81, 0.3)" stroke="rgba(156, 163, 175, 0.9)"/></svg>
            SurrealEstate<span className="auth-logo-accent" style={{ color: '#9ca3af' }}>Lab</span>
          </h1>
          <h2>Neues Passwort festlegen</h2>
          <p>Geben Sie Ihr neues Passwort ein</p>
        </div>

        <form className="auth-form" onSubmit={handleSubmit}>
          {error && (
            <div className="auth-error">
              <span>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              </span>
              <p>{error}</p>
            </div>
          )}

          <div className="auth-field">
            <label htmlFor="new-password">Neues Passwort</label>
            <input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Min. 8 Zeichen, 1 Grossbuchstabe, 1 Sonderzeichen"
              required
              autoComplete="new-password"
            />
          </div>

          <div className="auth-field">
            <label htmlFor="confirm-password">Passwort bestätigen</label>
            <input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Passwort wiederholen"
              required
              autoComplete="new-password"
            />
          </div>

          <p className="auth-pw-hint">
            Min. 8 Zeichen, mindestens 1 Grossbuchstabe und 1 Sonderzeichen
          </p>

          <button
            type="submit"
            className="auth-button"
            disabled={isLoading}
          >
            {isLoading ? 'Wird zurückgesetzt...' : 'Passwort zurücksetzen'}
          </button>
        </form>

        <div className="auth-footer">
          <Link to="/#lab-auth" className="auth-link-secondary">
            ← Zurück zur Anmeldung
          </Link>
        </div>
      </div>
    </div>
  );
}
